import React from 'react';
import { Button, ButtonProps } from 'reakit/Button';
import classNames from 'classnames';
import SpinnerMono from '@clearkit/icons/indicators/SpinnerMono';
import { Box, PolymorphicComponentProps } from 'react-polymorphic-box';

import { Enum } from '../../common/types';

export const CKButtonVariantColor = Enum(
  'default',
  'green',
  'red',
  'blue',
  'yellow'
);
export type CKButtonVariantColor = Enum<typeof CKButtonVariantColor>;

export const CKButtonVariant = Enum('bold', 'simple', 'tertiary');
export type CKButtonVariant = Enum<typeof CKButtonVariant>;

export interface CKButtonOwnProps
  extends Pick<ButtonProps, 'onClick' | 'disabled' | 'type'> {
  autoFocus?: boolean;
  className?: string;
  isSmall?: boolean;
  isDisabled?: React.InputHTMLAttributes<HTMLInputElement>['disabled'];
  isLoading?: boolean;
  children: React.ReactNode;
  variant?: CKButtonVariant;
  variantColor?: CKButtonVariantColor;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export interface CKButtonComposition {
  Variant: typeof CKButtonVariant;
  VariantColor: typeof CKButtonVariantColor;
}

const defaultProps = {
  variant: CKButtonVariant.simple,
  variantColor: CKButtonVariantColor.default,
};

const defaultElement = Button;

export type CKButtonProps<
  E extends React.ElementType
> = PolymorphicComponentProps<E, CKButtonOwnProps>;

export const CKButton = (React.forwardRef(
  <E extends React.ElementType = typeof defaultElement>(
    {
      className,
      children,
      leftIcon,
      rightIcon,
      isLoading,
      variant = defaultProps.variant,
      variantColor = defaultProps.variantColor,
      isSmall,
      isDisabled,
      // @ts-ignore
      as = defaultElement,
      ...props
    }: CKButtonProps<E>,
    innerRef: typeof props.ref
  ) => {
    const buttonClass = classNames(
      `ck-button`,
      `ck-button--${variant}--${variantColor.toLowerCase()}`,
      {
        'ck-button--small': isSmall,
      },
      className
    );

    const maybeLeftIcon = leftIcon ? (
      <span className="ck-button--left-icon">{leftIcon}</span>
    ) : null;
    const maybeRightIcon = rightIcon ? (
      <span className="ck-button--right-icon">{rightIcon}</span>
    ) : null;

    const buttonChildrenContent = isLoading ? (
      <SpinnerMono width="1.5em" height="1.5em" />
    ) : (
      <React.Fragment>
        {maybeLeftIcon}
        <span>{children}</span>
        {maybeRightIcon}
      </React.Fragment>
    );

    return (
      // @ts-ignore
      <Box
        ref={innerRef}
        disabled={isDisabled || isLoading}
        {...props}
        as={isDisabled ? defaultElement : as}
        className={buttonClass}
      >
        {buttonChildrenContent}
      </Box>
    );
  }
) as unknown) as CKButtonComposition &
  (<E extends React.ElementType = typeof defaultElement>(
    props: CKButtonProps<E>
  ) => JSX.Element);

CKButton.Variant = CKButtonVariant;
CKButton.VariantColor = CKButtonVariantColor;
